import React, { useState, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { toast } from "react-toastify";

import {
  getGrades,
  addGrade,
  editGrade,
  deleteGrade,
} from "../../api/gradeApi";
import DynamicDataTable from "../../common/components/Common/DataListing";

const GradeList = () => {
  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const [formData, setFormData] = useState({
    id: null,
    name: "",
    code: "",
    order: "",
    stage: "",
    is_final_year: false,
    is_active: true,
    max_capacity: null,
  });

  // 🔹 Fetch all grades on load
  useEffect(() => {
    fetchGrades();
  }, []);

  const fetchGrades = async () => {
    setLoading(true);
    try {
      const data = await getGrades();
      setGrades(data);
    } catch (err) {
      console.error("❌ Error fetching grades:", err);
      toast.error("Failed to fetch grades");
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Create Modal
  const handleCreate = () => {
    setIsEditing(false);
    setFormData({
      id: null,
      name: "",
      code: "",
      order: "",
      stage: "",
      is_final_year: false,
      is_active: true,
      max_capacity: null,
    });
    setIsModalVisible(true);
  };

  // 🔹 Edit Modal
  const handleEdit = (rowData) => {
    setIsEditing(true);
    setFormData(rowData);
    setIsModalVisible(true);
  };

  // 🔹 Delete (Fixed to instantly update UI)
  const handleDelete = async (id) => {
    if (!id) {
      toast.error("Invalid grade selected for delete");
      return;
    }

    try {
      setLoading(true);
      const res = await deleteGrade(id);
if (res?.success) {
  toast.success(res?.message || "Grades deleted successfully");
        // ✅ remove from frontend state instantly
        setGrades((prev) => prev.filter((e) => e.id !== id));
      } else {
        toast.error(res?.message || "Failed to delete grade");
      }
    } catch (err) {
      console.error("❌ Delete failed:", err);
      toast.error("Error deleting grade");
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Submit (Add / Edit)
  const handleSubmit = async () => {
    if (!formData.name || !formData.order) {
      toast.error("Name and Order are required");
      return;
    }

    try {
      setLoading(true);
      if (isEditing) {
        await editGrade(formData.id, formData);
        toast.success("Grade updated successfully");

        // update frontend data instantly
        setGrades((prev) =>
          prev.map((g) => (g.id === formData.id ? { ...g, ...formData } : g))
        );
      } else {
        const newGrade = await addGrade(formData);
        toast.success("Grade added successfully");

        // add new grade to UI
        setGrades((prev) => [newGrade, ...prev]);
      }

      setIsModalVisible(false);
    } catch (err) {
      console.error("❌ Save failed:", err);
      toast.error("Error saving grade");
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { field: "name", header: "Name" },
    { field: "order", header: "Order" },
    {
      header: "Max Capacity",
      body: (row) => row.max_capacity || "Unlimited",
    },
    {
      header: "Final Year",
      body: (row) => (row.is_final_year ? "Yes" : "No"),
    },
  ];

  return (
    <div>
      <DynamicDataTable
        data={grades}
        subject="Grade"
        columns={columns}
        onCreate={handleCreate}
        onEdit={handleEdit}
        onDelete={(row) => handleDelete(row.id)}
        loading={loading}
      />

      <Dialog
        header={isEditing ? "Edit Grade" : "Add Grade"}
        visible={isModalVisible}
        style={{ width: "40vw" }}
        onHide={() => setIsModalVisible(false)}
      >
        {loading ? (
          <div style={spinnerStyle}>
            <ProgressSpinner />
          </div>
        ) : (
          <div className="p-fluid">
            <div className="flex gap-3">
              <div className="p-field mb-2 w-6">
                <label htmlFor="name">Name</label>
                <InputText
                  id="name"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className="border-1 border-gray-300"
                />
              </div>

              <div className="p-field mb-2 w-6">
                <label htmlFor="order">Order</label>
                <InputText
                  id="order"
                  value={formData.order}
                  onChange={(e) =>
                    setFormData({ ...formData, order: e.target.value })
                  }
                  className="border-1 border-gray-300"
                />
              </div>
            </div>

            <div className="p-field mb-2">
              <label htmlFor="max_capacity">Max Capacity (leave empty for unlimited)</label>
              <InputNumber
                id="max_capacity"
                value={formData.max_capacity}
                onValueChange={(e) =>
                  setFormData({ ...formData, max_capacity: e.value })
                }
                min={1}
                placeholder="Unlimited"
                className="w-full"
              />
            </div>

            <div className="p-field mb-2 flex justify-content-between w-4">
              <label>Active</label>
              <InputSwitch
                checked={formData.is_active}
                onChange={(e) =>
                  setFormData({ ...formData, is_active: e.value })
                }
              />
            </div>

            <Button
              label={isEditing ? "Update" : "Add"}
              style={{ marginTop: "1rem" }}
              onClick={handleSubmit}
              disabled={loading}
            />
          </div>
        )}
      </Dialog>
    </div>
  );
};

const spinnerStyle = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: "100px",
};

export default GradeList;
